import { useEffect, useMemo, useRef, useState } from '@wordpress/element';
import { parse } from '@wordpress/blocks';
import { BlockPreview } from '@wordpress/block-editor';
import { MdFavoriteBorder, MdOutlineFavorite } from 'react-icons/md';
import { __ } from '@wordpress/i18n';

const TemplateCard = ({
	title,
	pattern,
	isFavorite,
	onToggleFavorite,
	onClick,
	style = {},
	premium,
	isProActive,
	handleUpgradeClick
}) => {
	const [isInserting, setIsInserting] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const observerRef = useRef(null);
	const cardRef = useRef(null);

	const isProAvailable = typeof isProActive === 'boolean';
	const isPremium = premium?.toLowerCase() === 'pro' && isProAvailable && isProActive === false;

	// Delay loading preview until visible (lazy rendering)
	useEffect(() => {
		if (!cardRef.current) return;

		observerRef.current = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observerRef.current?.disconnect();
				}
			},
			{ threshold: 0.1 }
		);

		observerRef.current.observe(cardRef.current);

		return () => observerRef.current?.disconnect();
	}, []);

	const blocks = useMemo(() => {
		try {
			let parsedBlocks = parse(pattern);
			return parsedBlocks.map((block) => {
				if (block.name === 'vayu-blocks/advance-container') {
					return {
						...block,
						attributes: {
							...block.attributes,
							variationSelected: true,
							skipVariationPicker: true
						}
					};
				}
				return block;
			});
		} catch (e) {
			console.error('Pattern parsing failed:', e);
			return [];
		}
	}, [pattern]);

	const handleInsert = async () => {
		setIsInserting(true);
		await onClick?.();
		setIsInserting(false);
	};

	return (
		<div className="vayu-template-card" style={style} ref={cardRef}>
			<div className="vayu-template-preview">
				{isVisible && blocks.length > 0 ? (
					<BlockPreview blocks={blocks} __experimentalLayout={{ type: 'default' }} />
				) : (
					<div className="vayu-preview-loader">Loading preview…</div>
				)}
			</div>

			<div className="vayu-template-card-footer">
				<div className="vayu-template-card-title">
					{title}
					{isPremium && (
						<span onClick={handleUpgradeClick} className="vayu-pro-badge" title="Pro Template">
							PRO
						</span>
					)}
				</div>
				<button
					type="button"
					className={`vayu-template-card-fav-btn ${isFavorite ? 'is-favorited' : ''}`}
					onClick={(e) => {
						e.stopPropagation();
						onToggleFavorite();
					}}
					aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
				>
					{isFavorite ? <MdOutlineFavorite size={20} /> : <MdFavoriteBorder size={20} />}
				</button>
			</div>

			<button
				type="button"
				className="vayu-template-insert-button"
				onClick={isPremium ? handleUpgradeClick : handleInsert}
				disabled={isInserting}
			>
				{isPremium
					? __('Unlock with Pro', 'vayu-blocks')
					: isInserting
					? (
						<>
							<span className="vayu-insert-spinner" />
							{__('Inserting...', 'vayu-blocks')}
						</>
					)
					: __('Insert', 'vayu-blocks')}
			</button>
		</div>
	);
};

export default TemplateCard;
